import React from "react";
import { fmtInt, ruleTip } from "@/lib/format";
import { ruleMeaning } from "@/lib/ruleMeanings";
import { RULE_HINTS } from "@/components/SignalExplainer";
import { RuleResult, cx, fmt, shortId } from "@/sentinel";

/* ------------------------------------------------------------------ */
/* Rule audit helpers shared by the Session desk, the mobile card and Thresholds */
/* ------------------------------------------------------------------ */

/** The decision sentence lives in lib/decisionText so the phone card can reuse it. */
export { decisionReason } from "@/lib/decisionText";

/** Path state from the engine ("PARTIAL_FADE", "partial fade") in the PATH chip vocabulary. */
export const pathOf = (p) => String(p || "UNCLASSIFIED").toUpperCase().replace(/_/g, " ");

/** Rule families in audit order L → E → T → C → B, then the families the engine adds after them. */
const FAMILIES = [
  ["L", "Liquidity", ["L1", "L2", "L3", "L4"]],
  ["E", "Expectancy", ["E1", "E2", "E3"]],
  ["T", "Tail and event risk", ["T1", "T2", "T3"]],
  ["C", "Confirmation", ["C1", "C2", "C3"]],
  ["B", "Entry gates", ["B1", "B2"]],
  ["S", "Slippage and fees", ["S1", "S2", "S3"]],
];
const EXTRA_FAMILY_NAMES = { DATA: "Data integrity", KS: "Kill switch", KILL: "Kill switch", EDGE: "Edge filter" };
const normResult = (v) => {
  const s = String(v || "NA").toUpperCase();
  return s === "N/A" ? "NA" : s;
};

/**
 * Observed vs threshold for the rules whose inputs are on the candidate record.
 * `gap` is signed so that a negative value is a shortfall. Anything the record can't evidence is "—".
 */
export function ruleEvidence(code, c, cp) {
  const none = { obs: "—", thr: "—", gap: "—" };
  if (!c) return none;
  const legs = c.legs || [];
  const spot = c.model_params?.spot;
  switch (code) {
    case "L1": {
      if (!legs.length || !c.width) return none;
      const thr = Math.min(0.1 * c.width, 0.6);
      const sp = Math.max(...legs.map((l) => (l.spread != null ? l.spread : (l.ask ?? 0) - (l.bid ?? 0))));
      return { obs: fmt(sp), thr: `≤ ${fmt(thr)}`, gap: fmt(thr - sp, "price", { signed: true }) };
    }
    case "L3": {
      const leg = legs.find((l) => l.strike === c.short_strike);
      if (!leg) return none;
      return { obs: `OI ${fmtInt(leg.oi)} · vol ${fmtInt(leg.volume)}`, thr: "OI ≥ 500 or vol ≥ 200", gap: "—" };
    }
    case "L4": {
      if (spot == null) return none;
      const dist = Math.max(Math.abs(c.short_strike - spot), Math.abs(c.long_strike - spot));
      const lim = 0.03 * spot;
      return { obs: `${fmt(dist, "pts")} pts`, thr: `≤ ${fmt(lim, "pts")} pts`, gap: `${fmt(lim - dist, "pts", { signed: true })} pts` };
    }
    case "E1": {
      if (c.ev_pct == null) return none;
      const floor = cp?.e1_floor;
      if (floor == null) return { obs: fmt(c.ev_pct * 100, "pct", { signed: true }), thr: "≥ +8.0% (12% if T3)", gap: "—" };
      return { obs: fmt(c.ev_pct * 100, "pct", { signed: true }), thr: `≥ ${fmt(floor * 100, "pct", { signed: true })}`, gap: fmt((c.ev_pct - floor) * 100, "pct", { signed: true }) };
    }
    case "E2": {
      if (c.is_credit) {
        if (c.credit_ratio == null) return none;
        return { obs: fmt(c.credit_ratio * 100, "pct") + " of width", thr: "≥ 20.0% of width", gap: fmt((c.credit_ratio - 0.2) * 100, "pct", { signed: true }) };
      }
      if (!c.max_loss) return none;
      const r = c.max_profit / Math.abs(c.max_loss);
      return { obs: `${fmt(r, "ratio")}×`, thr: "≥ 1.20×", gap: `${fmt(r - 1.2, "ratio", { signed: true })}×` };
    }
    case "E3": {
      if (c.p_profit == null) return none;
      const floor = c.is_credit ? (cp?.t3_active ? 0.7 : 0.65) : 0.4;
      return { obs: fmt(c.p_profit, "prob"), thr: `≥ ${fmt(floor, "prob")}`, gap: fmt(c.p_profit - floor, "prob", { signed: true }) };
    }
    case "T1": {
      if (!c.is_credit || c.p_touch == null) return { obs: "—", thr: "Credit spreads only", gap: "—" };
      return { obs: fmt(c.p_touch, "prob"), thr: "≤ 0.35", gap: fmt(0.35 - c.p_touch, "prob", { signed: true }) };
    }
    default:
      return none;
  }
}

/** Candidate rule results as AuditMatrix families, every family shown even after a FAIL. */
export function ruleFamilies(rules, candidate, checkpoint) {
  const known = new Set(FAMILIES.flatMap(([, , codes]) => codes));
  // The plain sentence leads; the technical threshold text stays as a tooltip (`tech`).
  const row = (code) => {
    const ev = ruleEvidence(code, candidate, checkpoint);
    return { id: code, text: ruleMeaning(code) || ruleTip[code] || code, tech: ruleTip[code] || null, obs: ev.obs, thr: ev.thr, res: normResult(rules?.[code]) };
  };
  const fams = FAMILIES.map(([fam, famName, codes]) => ({ fam, famName, rows: codes.map(row) }));
  const extras = {};
  Object.keys(rules || {}).filter((k) => !known.has(k)).sort().forEach((k) => {
    const fam = k.replace(/[-\d]+$/, "") || k;
    (extras[fam] = extras[fam] || []).push(k);
  });
  Object.entries(extras).forEach(([fam, codes]) => fams.push({ fam, famName: EXTRA_FAMILY_NAMES[fam] || fam, rows: codes.map(row) }));
  return fams;
}

/**
 * Threshold audit in family order (AuditMatrix markup, with a data-testid on every rule result).
 * `stacked` is the mobile layout: rule, then "observed vs threshold" on a second line, then result.
 */
export function AuditRow({ rules, candidate, checkpoint, stacked = false, id }) {
  if (!rules) return null;
  const families = ruleFamilies(rules, candidate, checkpoint);
  const all = families.flatMap((f) => f.rows);
  const count = (r) => all.filter((x) => x.res === r).length;
  const label = `Threshold audit for ${candidate?.candidate_id || "candidate"}`;
  const result = (r) => (
    <span title={r.tech || r.text}>
      <RuleResult result={r.res} data-testid={`rule-${r.id}`}><span className="sn-vh">{r.id} </span>{r.res}</RuleResult>
    </span>
  );
  return (
    <div data-testid="audit-row" id={id} style={{ minWidth: 0 }}>
      <div style={{ overflowX: "auto" }}>
        <table className={cx("sn-audit", stacked && "sn-audit-stacked")} aria-label={label}>
          <thead>
            {stacked
              ? <tr><th scope="col">Rule</th><th scope="col">Check · observed vs threshold</th><th scope="col">Result</th></tr>
              : <tr><th scope="col">Rule</th><th scope="col">Checks</th><th scope="col" className="r">Observed</th><th scope="col" className="r">Threshold</th><th scope="col">Result</th></tr>}
          </thead>
          <tbody>
            {families.map((f) => (
              <FamilyRows key={f.fam} f={f} stacked={stacked} result={result} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="sn-audit-summary">
        <span><span className="sn-num">{count("PASS")}</span> PASS</span>
        <span><span className="sn-num">{count("FAIL")}</span> FAIL</span>
        <span><span className="sn-num">{count("NA")}</span> NA</span>
        <span><span className="sn-num">{count("UNCONFIRMED")}</span> UNCONFIRMED</span>
        {!stacked && <span>Evaluated L → E → T → C → B · all families shown, even after a FAIL</span>}
      </div>
    </div>
  );
}

function FamilyRows({ f, stacked, result }) {
  return (
    <>
      <tr className="sn-fam"><td colSpan={stacked ? 3 : 5}>{f.fam} · {f.famName}</td></tr>
      {f.rows.map((r) => stacked ? (
        <tr key={r.id} data-result={r.res}>
          <td className="rid">{r.id}</td>
          <td className="rtext" title={r.tech || undefined}>{r.text}{(r.obs !== "—" || r.thr !== "—") && <span className="sn-audit-vals"><b>{r.obs}</b> vs {r.thr}</span>}</td>
          <td>{result(r)}</td>
        </tr>
      ) : (
        <tr key={r.id} data-result={r.res}>
          <td className="rid">{r.id}</td>
          <td className="rtext" title={r.tech || undefined}>{r.text}</td>
          <td className="r num">{r.obs}</td>
          <td className="r num" style={{ color: "var(--text-secondary)" }}>{r.thr}</td>
          <td>{result(r)}</td>
        </tr>
      ))}
    </>
  );
}

/**
 * Evidence props for BlockingRuleCallout from the checkpoint and its nearest candidate.
 * The plain meaning is the headline; the technical threshold text and the hint sit under it.
 */
export function blockingProps(rule, candidate, checkpoint) {
  const ev = ruleEvidence(rule, candidate, checkpoint);
  const known = ev.obs !== "—";
  const res = candidate?.rules?.[rule];
  const hint = RULE_HINTS[rule] || "The engine refused entry on this rule. The full audit shows every family.";
  const tech = ruleTip[rule];
  return {
    rule,
    title: ruleMeaning(rule) || tech || `Rule ${rule}`,
    plain: tech && ruleMeaning(rule) ? `${hint} Technical check: ${tech}.` : hint,
    observed: known ? ev.obs : res ? normResult(res) : "Not met",
    threshold: known ? ev.thr : "Must pass",
    gap: ev.gap,
    candidate: candidate ? `${shortId(candidate.candidate_id)} · ${fmt(candidate.short_strike, "strike")} / ${fmt(candidate.long_strike, "strike")}` : undefined,
  };
}

/** "O:SPXW260819P05975000" — the OCC symbol for a same-day SPXW leg. */
export function occSymbol(sessionDate, right, strike) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sessionDate || "") || strike == null) return "—";
  const ymd = sessionDate.slice(2).replace(/-/g, "");
  return `O:SPXW${ymd}${String(right || "P").toUpperCase().charAt(0)}${String(Math.round(Number(strike) * 1000)).padStart(8, "0")}`;
}
